import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { openApiFile } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import {
  attachOrderProof,
  getOrderCompletion,
  orderProofContentPath,
  removeOrderProof,
  saveOrderCompletion,
  setOrderProofLocation,
  submitOrderCompletion,
  type OrderWorkCompletion,
} from '../../api/orderWorkApi.js';

/**
 * "Job completed" on an order that needs no dealer: the vendor attaches his
 * proofs and saves, then submits. Verification is the workflow the tenant
 * bound; once submitted the completion is his to read, no longer to change.
 */
export function OrderWorkCompletionPage(): ReactNode {
  const { completionId = '' } = useParams<{ completionId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const { decimal } = useBusinessNumber();

  const [c, setC] = useState<OrderWorkCompletion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [proofType, setProofType] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [remarks, setRemarks] = useState('');
  // The position being typed in for one proof, by a login allowed to.
  const [placing, setPlacing] = useState<{ proofId: string; lat: string; lng: string } | null>(null);

  const load = useCallback(async () => {
    const r = await getOrderCompletion(completionId, token);
    setC(r);
    setRemarks(r.remarks ?? '');
    return r;
  }, [completionId, token]);

  useEffect(() => {
    load().catch((e) => setError((e as Error).message));
  }, [load]);

  async function run(work: () => Promise<unknown>, done: string): Promise<void> {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await work();
      await load();
      setNote(done);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!c) {
    return (
      <main aria-label="order-completion">
        {error ? <p role="alert">{error}</p> : <p>{t('common.loading')}</p>}
      </main>
    );
  }

  const draft = c.status === 'DRAFT';
  const ref = `${c.order.requestNo} / ${c.order.lineNumber}`;

  return (
    <main aria-label="order-completion">
      <p><Link to="/vendor-ops/dashboard" aria-label="back-to-dashboard">{t('vops.orderWork.back')}</Link></p>
      <h1>{t('vops.orderWork.title', { ref })}</h1>

      <dl aria-label="order-fields">
        <dt>{t('vops.orderWork.branding')}</dt>
        <dd>{[c.order.typeName, c.order.itemName].filter(Boolean).join(' / ') || '\u2014'}</dd>
        <dt>{t('vops.orderWork.quantity')}</dt>
        <dd>{decimal(c.order.quantity)} {unitLabel(c.order.uomCode)}</dd>
        <dt>{t('vops.orderWork.workOrder')}</dt>
        <dd>{c.order.woNumber ?? '\u2014'}</dd>
        <dt>{t('vops.orderWork.colStatus')}</dt>
        <dd aria-label="completion-status">{t(`vops.orderWork.status.${c.status}`)}</dd>
        {c.submittedAt ? (<><dt>{t('vops.orderWork.submittedAt')}</dt><dd>{at(c.submittedAt)}</dd></>) : null}
      </dl>

      {error ? <p role="alert">{error}</p> : null}
      {note ? <p role="status">{note}</p> : null}

      <section aria-label="proofs">
        <h2>{t('vops.orderWork.proofs')}</h2>
        {c.evidence.requiresPhoto || c.evidence.requiresGps ? (
          <p aria-label="evidence-required">
            {c.evidence.requiresPhoto && c.evidence.requiresGps
              ? t('vops.orderWork.needs.photoAndPosition')
              : c.evidence.requiresPhoto
                ? t('vops.orderWork.needs.photo')
                : t('vops.orderWork.needs.position')}
          </p>
        ) : null}
        {c.proofs.length === 0 ? (
          <p aria-label="proofs-empty">{t('vops.orderWork.noProofs')}</p>
        ) : (
          <ul aria-label="proof-list">
            {c.proofs.map((p) => (
              <li key={p.proofId} aria-label={`proof-${p.proofId}`}>
                {p.proofTypeName ?? '\u2014'} · {p.fileName}{' '}
                <span aria-label={`proof-position-${p.proofId}`}>
                  {p.latitude != null && p.longitude != null
                    ? t(`vops.orderWork.position.${p.locationSource ?? 'PHOTO'}`, {
                        lat: p.latitude.toFixed(6), lng: p.longitude.toFixed(6),
                      })
                    : t('vops.orderWork.position.none')}
                </span>{' '}
                <Button type="button" variant="secondary" disabled={busy}
                        onClick={() => openApiFile(orderProofContentPath(c.completionId, p.proofId), token)
                          .catch((e) => setError((e as Error).message))}>
                  {t('vops.orderWork.view')}
                </Button>{' '}
                {draft && c.mayEnterLocation && p.locationSource !== 'PHOTO' ? (
                  <Button type="button" variant="secondary" disabled={busy}
                          aria-label={`enter-position-${p.proofId}`}
                          onClick={() => setPlacing({
                            proofId: p.proofId,
                            lat: p.latitude != null ? String(p.latitude) : '',
                            lng: p.longitude != null ? String(p.longitude) : '',
                          })}>
                    {t('vops.orderWork.position.enter')}
                  </Button>
                ) : null}{' '}
                {draft ? (
                  <Button type="button" variant="danger" disabled={busy}
                          aria-label={`remove-proof-${p.proofId}`}
                          onClick={() => void run(() => removeOrderProof(c.completionId, p.proofId, token), t('vops.orderWork.removed'))}>
                    {t('vops.orderWork.remove')}
                  </Button>
                ) : null}
                {placing?.proofId === p.proofId ? (
                  <div aria-label={`position-form-${p.proofId}`}>
                    <label htmlFor={`lat-${p.proofId}`}>{t('vops.orderWork.position.latitude')}</label>
                    <input id={`lat-${p.proofId}`} inputMode="decimal" value={placing.lat} disabled={busy}
                           onChange={(e) => setPlacing({ ...placing, lat: e.target.value })} />
                    <label htmlFor={`lng-${p.proofId}`}>{t('vops.orderWork.position.longitude')}</label>
                    <input id={`lng-${p.proofId}`} inputMode="decimal" value={placing.lng} disabled={busy}
                           onChange={(e) => setPlacing({ ...placing, lng: e.target.value })} />
                    <Button type="button" variant="primary" disabled={busy || !placing.lat || !placing.lng}
                            aria-label={`save-position-${p.proofId}`}
                            onClick={() => void run(async () => {
                              await setOrderProofLocation(c.completionId, p.proofId,
                                Number(placing.lat), Number(placing.lng), token);
                              setPlacing(null);
                            }, t('vops.orderWork.position.saved'))}>
                      {t('vops.orderWork.save')}
                    </Button>{' '}
                    <Button type="button" variant="secondary" disabled={busy} onClick={() => setPlacing(null)}>
                      {t('vops.orderWork.position.cancel')}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {draft ? (
          <div aria-label="attach-proof">
            <label htmlFor="proof-type">{t('vops.orderWork.proofType')}</label>
            <select id="proof-type" value={proofType} disabled={busy}
                    onChange={(e) => setProofType(e.target.value)}>
              <option value="">{t('vops.orderWork.choose')}</option>
              {c.proofTypes.map((pt) => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
            </select>
            <label htmlFor="proof-file">{t('vops.orderWork.file')}</label>
            <input id="proof-file" type="file" disabled={busy}
                   onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <Button type="button" variant="primary" disabled={busy || !proofType || !file}
                    aria-label="attach-proof-btn"
                    onClick={() => void run(async () => {
                      await attachOrderProof(c.completionId, proofType, file as File, token);
                      setFile(null);
                    }, t('vops.orderWork.attached'))}>
              {t('vops.orderWork.attach')}
            </Button>
          </div>
        ) : null}
      </section>

      <section aria-label="completion-remarks">
        <label htmlFor="completion-remarks">{t('vops.orderWork.remarks')}</label>
        <textarea id="completion-remarks" value={remarks} disabled={!draft || busy}
                  onChange={(e) => setRemarks(e.target.value)} />
      </section>

      {draft ? (
        <nav aria-label="completion-actions" className="tl-toolbar">
          <Button type="button" variant="secondary" disabled={busy} aria-label="save-completion"
                  onClick={() => void run(() => saveOrderCompletion(c.completionId, remarks, token), t('vops.orderWork.saved'))}>
            {t('vops.orderWork.save')}
          </Button>
          {/* Submit appears only once the proofs are saved, as the owner set it. */}
          {c.savedAt ? (
            <Button type="button" variant="primary" disabled={busy} aria-label="submit-completion"
                    onClick={() => void run(() => submitOrderCompletion(c.completionId, token), t('vops.orderWork.submitted'))}>
              {t('vops.orderWork.submit')}
            </Button>
          ) : null}
        </nav>
      ) : c.workflowInstanceId ? (
        <p><Link to="/billing/completed-jobs" aria-label="verification-trail">{t('vops.orderWork.trail')}</Link></p>
      ) : null}
    </main>
  );
}
